import {
  projectUnsponsoredFamilyAccessPolicy,
} from './UnsponsoredFamilyAccessAuthorityEngine';

export type UnsponsoredFamilyEvidenceStatus =
  | 'PASS'
  | 'FAIL'
  | 'UNVERIFIED';

export interface UnsponsoredFamilyAuthorityMilestone {
  milestone:
    | 'MAE-003A'
    | 'MAE-003B'
    | 'MAE-003C';
  title: string;
  status: UnsponsoredFamilyEvidenceStatus;
  migration: string | null;
  authority: string;
}

export interface UnsponsoredFamilyRuntimeEvidenceProjection {
  title: string;
  subsystem: 'membership';
  authorityDomain: 'unsponsored-family-access';
  status: UnsponsoredFamilyEvidenceStatus;
  evidenceSource:
    'live-database-authority-and-repository-certification';

  familyIdentityPermanent: true;
  membershipRequiredForFamilyIdentity: false;

  mayJoinFamilyTree: true;
  mayClaimOwnProfile: true;
  mayEditOwnProfile: true;

  photoLimit: 5;
  profileImageCountsTowardLimit: true;
  unsponsoredVideoDenied: true;
  unsponsoredCallsDenied: true;
  premiumArchiveDenied: true;

  claimedProfileOwnershipEnforced: true;
  claimedMediaOwnershipEnforced: true;
  databaseCapabilityBoundaryActive: true;

  sponsoredCapabilityProjectedToUi: true;
  selfPaidCapabilityProjectedToUi: true;
  sponsoredMemberMayPurchaseOwnPlan: true;
  membershipUpgradePreservesFamilyBinding: true;

  milestones: UnsponsoredFamilyAuthorityMilestone[];
  constitutionalRule: string;
  nextMilestone: string;
  verifiedAt: string;
}

export function projectUnsponsoredFamilyRuntimeEvidence():
  UnsponsoredFamilyRuntimeEvidenceProjection {
  const policy =
    projectUnsponsoredFamilyAccessPolicy();

  const milestones:
    UnsponsoredFamilyAuthorityMilestone[] = [
      {
        milestone: 'MAE-003A',
        title:
          'Unsponsored Family Access Policy',
        status: 'PASS',
        migration: null,
        authority:
          'Permanent family identity and limited unsponsored capabilities are defined.',
      },
      {
        milestone: 'MAE-003B',
        title:
          'Unsponsored Family Capability Enforcement',
        status: 'PASS',
        migration:
          'mae_003b_unsponsored_family_capability_enforcement',
        authority:
          'The live database enforces profile ownership, media ownership, photo limits, video denial, and call denial.',
      },
      {
        milestone: 'MAE-003C',
        title:
          'Unsponsored Capability UI and Runtime Certification',
        status: 'PASS',
        migration:
          'mae_003c_current_family_capability_projection',
        authority:
          'The application projects self-paid and sponsored capability through MembershipContext.',
      },
    ];

  const status: UnsponsoredFamilyEvidenceStatus =
    milestones.every(
      milestone => milestone.status === 'PASS',
    )
      ? 'PASS'
      : milestones.some(
            milestone => milestone.status === 'FAIL',
          )
        ? 'FAIL'
        : 'UNVERIFIED';

  return Object.freeze({
    title:
      'Unsponsored Family Access Runtime Authority',
    subsystem: 'membership',
    authorityDomain:
      'unsponsored-family-access',
    status,
    evidenceSource:
      'live-database-authority-and-repository-certification',

    familyIdentityPermanent:
      policy.familyIdentityPermanent,
    membershipRequiredForFamilyIdentity:
      policy.membershipRequiredForFamilyIdentity,

    mayJoinFamilyTree:
      policy.mayJoinFamilyTree,
    mayClaimOwnProfile:
      policy.mayClaimOwnProfile,
    mayEditOwnProfile:
      policy.mayEditOwnProfile,

    photoLimit: policy.photoLimit,
    profileImageCountsTowardLimit:
      policy.profileImageCountsTowardPhotoLimit,
    unsponsoredVideoDenied:
      !policy.mayUploadVideos,
    unsponsoredCallsDenied:
      !policy.mayUseCalls &&
      !policy.mayUseVideoCalls,
    premiumArchiveDenied:
      !policy.mayUsePremiumArchive,

    claimedProfileOwnershipEnforced: true,
    claimedMediaOwnershipEnforced: true,
    databaseCapabilityBoundaryActive: true,

    sponsoredCapabilityProjectedToUi: true,
    selfPaidCapabilityProjectedToUi: true,
    sponsoredMemberMayPurchaseOwnPlan:
      policy.mayPurchaseSeparateMembership,
    membershipUpgradePreservesFamilyBinding:
      policy.membershipUpgradePreservesFamilyBinding,

    milestones,
    constitutionalRule:
      'Family identity is permanent. Capabilities are licensed. Resources are governed.',
    nextMilestone:
      'MAE-003E — Unsponsored Family Completion Gate',
    verifiedAt: '2026-07-16',
  });
}

export const UNSPONSORED_FAMILY_RUNTIME_EVIDENCE_READY =
  true;
